import { Film } from 'lucide-react'
import { siteText } from '../siteText'
import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api'
import { useAsync, useDebouncedValue } from '../hooks'
import { WorkspaceLoading } from '../components/ui'
import { WorkspaceActions, WorkspaceSearch } from '../components/WorkspaceHeader'
import { LibraryAssetRailItem, LibraryAssetDetail } from '../components/AssetLibrary'
import { ContentPlan } from '../components/ContentPlan'
import { CalendarClipDetail } from '../components/CalendarClipDetail'
import { useCalendarDrag } from '../calendarDrag'
import { calendarOrder, calendarPosition } from '../components/Editorial'
import type { LibraryAsset as LibraryItem, User } from '../types'

export function CalendarView({ user, focusClipId, onClip }: { user: User; focusClipId: string | null; onClip: (id: string) => void }) {
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const detailTrigger = useRef<HTMLElement | null>(null)
  const previousFocusClipId = useRef(focusClipId)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const saveInFlight = useRef(false)
  const [saved, setSaved] = useState('')
  const debounced = useDebouncedValue(query)
  const library = useAsync(api.library, [], { pollMs: 3000 })
  const [assetId, setAssetId] = useState<string | null>(null)
  const clips = useAsync(api.clips, [], { pollMs: 3000 })
  const jobs = useAsync(api.jobs, [], { pollMs: 3000 })
  const canManage = true
  const external = (library.data || []).filter(asset => asset.origin === 'external')
  const calendarItems = [...(clips.data || []).filter(clip => clip.status !== 'complete' ||
    !clip.versions.some(version => version.id === clip.latest_version_id && version.library_deleted)), ...external]
  const detailAsset = external.find(asset => asset.id === assetId)
  function updateAsset(asset: LibraryItem) { library.setData(items => items?.map(item => item.id === asset.id ? asset : item) || [asset]) }
  function openAsset(asset: LibraryItem) { if (asset.origin === 'motion' && asset.clip_id) openDetail(asset.clip_id); else setAssetId(asset.id) }
  const detailClip = clips.data?.find(clip => clip.id === focusClipId)
  function openDetail(id: string) {
    if (external.some(asset => asset.id === id)) { setAssetId(id); return }
    detailTrigger.current = document.activeElement as HTMLElement | null
    setSelectedId(id); onClip(id)
  }
  useEffect(() => {
    if (previousFocusClipId.current && !focusClipId) requestAnimationFrame(() => { if (detailTrigger.current?.isConnected) detailTrigger.current.focus({ preventScroll: true }) })
    previousFocusClipId.current = focusClipId
  }, [focusClipId])
  const readyAssets = useMemo(() => (library.data || []).filter(asset =>
    !debounced || `${asset.title} ${asset.description || ''}`.toLowerCase().includes(debounced.toLowerCase()),
  ), [library.data, debounced])
  async function moveClip(id: string, day: string | null, beforeId?: string) {
    const clip = calendarItems.find(item => item.id === id)
    if (!canManage || saveInFlight.current || !clip) return
    const ordered = calendarItems.filter(item => (item.editorial_plan?.planned_day || null) === day && item.id !== id).sort(calendarOrder)
    const beforeIndex = beforeId ? ordered.findIndex(item => item.id === beforeId) : -1
    const index = beforeIndex >= 0 ? beforeIndex : ordered.length
    const previous = index > 0 ? calendarPosition(ordered[index - 1]) : null
    const next = ordered[index] ? calendarPosition(ordered[index]) : null
    const position = day === null ? null : next != null ? (previous != null ? (previous + next) / 2 : next - 1024) : (previous ?? 0) + 1024
    saveInFlight.current = true; setSaving(true); setError(''); setSaved('')
    try {
      if ('origin' in clip) {
        updateAsset(await api.libraryEditorial(id, { revision: clip.editorial_plan.revision, planned_day: day, position }))
        setSelectedId(id); setSaved(day ? `${clip.title} scheduled for ${day}.` : `${clip.title} removed from the calendar.`)
        return
      }
      const updated = await api.editorial(id, { revision: clip.editorial_plan?.revision || 0, planned_day: day, position })
      await clips.refresh()
      // A poll started before the save can finish afterward. Keep the save response.
      clips.setData(current => current?.map(item => item.id === id ? updated : item) || [updated])
      setSelectedId(id)
      setSaved(day ? `${clip.title} scheduled for ${new Date(`${day}T12:00:00`).toLocaleDateString()}.` : `${clip.title} removed from the calendar.`)
    } catch (reason) { await clips.refresh(); throw reason }
    finally { saveInFlight.current = false; setSaving(false) }
  }
  const drag = useCalendarDrag(canManage && !saving && !detailClip && !detailAsset, (id, day, beforeId) => {
    void moveClip(id, day, beforeId).catch(reason => setError(reason instanceof Error ? reason.message : 'The date could not be saved.'))
  })
  if ((clips.data === null && !clips.error) || (jobs.data === null && !jobs.error)) return <div className="page-content calendar-page"><WorkspaceLoading label="Loading calendar" /></div>
  return <div ref={drag.root} {...drag.handlers} className="page-content calendar-page">
    {!detailClip ? <WorkspaceActions><WorkspaceSearch value={query} onChange={setQuery} label="Search calendar assets" inputLabel="Search ready assets" /></WorkspaceActions> : null}
    {detailAsset ? <LibraryAssetDetail key={detailAsset.id} asset={detailAsset} onClose={() => setAssetId(null)} onUpdate={updateAsset} onMove={moveClip} /> : null}
    <div className="calendar-workspace" hidden={Boolean(detailClip)}>
      <section className="clip-rail calendar-asset-rail" aria-label="Ready to plan">
        {error || clips.error || library.error ? <p role="alert">{error || clips.error || library.error}</p> : null}
        <div className="clip-list calendar-asset-list" role="region" aria-label="Approved assets" tabIndex={0}>
          {readyAssets.map(asset => <LibraryAssetRailItem key={asset.id} asset={asset} onOpen={openAsset} draggable={canManage && !saving} selected={(asset.clip_id || asset.id) === selectedId} />)}
          {!readyAssets.length ? <p>{library.data === null && !library.error ? 'Loading approved assets…' : query ? 'No matching assets.' : 'Upload approved media to the asset library. Motion videos appear after final approval.'}</p> : null}
        </div>
      </section>
      <ContentPlan clips={calendarItems} visibleClips={calendarItems} user={user} selectedId={selectedId} busy={saving} dragDay={drag.day} onMove={moveClip} onOpen={openDetail} />
    </div>
    {detailClip ? <CalendarClipDetail key={detailClip.id} clip={detailClip} jobs={jobs.data || []} user={user} onUpdate={updated => clips.setData(current => current?.map(item => item.id === updated.id ? updated : item) || [updated])} /> : null}
    <span className="sr-only" role="status">{siteText(saved)}</span>
    {drag.preview ? <div className="calendar-drag-preview" aria-hidden="true" style={{ left: Math.max(8, Math.min(drag.preview.x + 14, window.innerWidth - 196)), top: Math.max(8, Math.min(drag.preview.y + 14, window.innerHeight - 226)) }}>
      {drag.preview.image ? <img src={drag.preview.image} alt="" draggable={false} /> : <span className="calendar-drag-placeholder"><Film size={26} /></span>}
      <strong>{siteText(calendarItems.find(clip => clip.id === drag.preview?.id)?.title || (library.data || []).find(asset => (asset.clip_id || asset.id) === drag.preview?.id)?.title)}</strong>
    </div> : null}
  </div>
}
